<script setup>
import { stripDiacritics } from "@/lib/sentenceUtils";
import { useAudioPreloader } from "@/composables/useAudioPreloader";
import { splitSentence, stripLeadingPunctuation } from "@/lib/sentenceUtils";
/* global defineProps, defineEmits, defineExpose */
/*
  FlashcardDeck.vue
  - Reusable flashcard deck with demo mode
  - Preserves highlighting animations for correct/incorrect/synonym answers
  - Plays audio when URLs are present; falls back to Web Speech API TTS in demo mode
  - In demo mode, there are NO Supabase or FSRS side-effects

  Props:
    - limit: number (default 10)         => maximum cards to show
    - preloadedCards: Flashcard[]        => if provided and non-empty, demo mode is enabled
    - autoFetch: boolean (default true)  => ignored in demo mode
    - showStateDots: boolean (default true)
    - showMorpho: boolean (default true)
    - defaultShowSentenceTranslation: boolean (default true)
    - ttsVoiceHint: string (optional name/language hint for speechSynthesis voice selection)

  Emits:
    - card-reviewed: { id, correct, durationMs }
    - synonym-entered: void
    - index-changed: { index, total }
    - loaded: { total }
    - error: { error }
*/

import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

const audioPreloader = useAudioPreloader({
  maxEntries: 40,
  concurrency: 2,
});

// Props / Emits
const props = defineProps({
  limit: { type: Number, default: 10 },
  preloadedCards: { type: Array, default: () => [] },
  autoFetch: { type: Boolean, default: true },
  showStateDots: { type: Boolean, default: true },
  showMorpho: { type: Boolean, default: true },
  defaultShowSentenceTranslation: { type: Boolean, default: true },
  singleAttempt: { type: Boolean, default: false },
  bare: { type: Boolean, default: false },
});
const emit = defineEmits([
  "card-reviewed",
  "synonym-entered",
  "index-changed",
  "loaded",
  "error",
]);

// Demo mode: enabled when preloaded cards are provided
const demoMode = computed(
  () => Array.isArray(props.preloadedCards) && props.preloadedCards.length > 0
);

// State
const currentFlashcards = ref([]);
const currentIndex = ref(0);

const inputEl = ref(null);
const inputValue = ref("");
const answerStatus = ref(null); // null | 'correct' | 'incorrect'
const isSynonymAnswer = ref(false);
const showSentenceTranslation = ref(!!props.defaultShowSentenceTranslation);
const attemptStartedAt = ref(0);
const firstAttemptResolved = ref(false);

// Autofocus directive + responsive width sizing
const DEFAULT_INPUT_PX = 56;
const MIN_INPUT_PX = 40;
const EXTRA_PX = 14;

// Measure width of the current string with the input's font
function measureInputPx(text) {
  try {
    const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
    const font = el
      ? getComputedStyle(el).font
      : getComputedStyle(document.body).font;
    measureInputPx._canvas =
      measureInputPx._canvas || document.createElement("canvas");
    const ctx = measureInputPx._canvas.getContext("2d");
    if (!ctx) {
      return Math.max(
        MIN_INPUT_PX,
        Math.ceil(String(text || "").length * 8 + EXTRA_PX)
      );
    }
    ctx.font = font || "16px system-ui";
    const m = ctx.measureText(String(text || ""));
    return Math.max(MIN_INPUT_PX, Math.ceil(m.width + EXTRA_PX));
  } catch {
    return Math.max(
      MIN_INPUT_PX,
      Math.ceil(String(text || "").length * 8 + EXTRA_PX)
    );
  }
}
function applyDynamicWidth(textOverride) {
  const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
  if (!el) return;
  const text =
    textOverride !== undefined
      ? String(textOverride)
      : String(inputValue.value || "");
  const px = text ? measureInputPx(text) : DEFAULT_INPUT_PX;
  el.style.width = Math.max(px, MIN_INPUT_PX) + "px";
}
const onInputRaw = (e) => {
  applyDynamicWidth(e?.target?.value ?? "");
};
const onCompositionStart = () => {
  /* no-op */
};
const onCompositionUpdate = (e) => {
  applyDynamicWidth(e?.target?.value ?? "");
};
const onCompositionEnd = (e) => {
  applyDynamicWidth(e?.target?.value ?? "");
};

const vFocus = {
  mounted(el) {
    try {
      if (document.activeElement !== el) {
        el.focus();
        if (answerStatus.value == null && typeof el.select === "function") {
          el.select();
        } else if (typeof el.setSelectionRange === "function") {
          const len = (el.value || "").length;
          el.setSelectionRange(len, len);
        }
      }
      applyDynamicWidth();
    } catch (_e) {
      /* ignore */
    }
  },
  updated(el) {
    try {
      // Only adjust focus if not already focused; do not re-select text on every update
      if (document.activeElement !== el) {
        el.focus();
        if (typeof el.setSelectionRange === "function") {
          const len = (el.value || "").length;
          el.setSelectionRange(len, len);
        }
      }
      applyDynamicWidth();
    } catch (_e) {
      /* ignore */
    }
  },
};
const onResize = () => requestAnimationFrame(() => applyDynamicWidth());
if (typeof window !== "undefined") {
  window.addEventListener("resize", onResize, { passive: true });
}
onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", onResize);
  }
  try {
    audioPreloader.dispose();
  } catch (_e) {
    /* ignore */
  }
});

// Helpers
// Using shared stripDiacritics from "@/lib/sentenceUtils"

watch(
  () => [currentIndex.value, currentFlashcards.value.length],
  () => {
    audioPreloader.queueForCards(currentFlashcards.value, {
      index: currentIndex.value,
      ahead: 6,
      behind: 1,
    });
  },
  { immediate: true }
);

const currentCard = computed(
  () => currentFlashcards.value[currentIndex.value] || null
);

const activeDots = computed(() => {
  const s = Number(currentCard.value?.state ?? 0);
  if (s === 0) return 0; // New
  if (s === 1) return 1; // Learn
  if (s === 2) return 3; // Review
  if (s === 3) return 2; // Re-learn
  return 1; // Unknown
});

const morphoInfo = computed(() => {
  const c = currentCard.value || {};
  const parts = [
    c.part_of_speech || null,
    c.tense || null,
    c.number || null,
    c.person || null,
    c.measure_word || null,
  ]
    .map((x) => (typeof x === "string" ? x.trim() : x))
    .filter((x) => x && !(typeof x === "string" && x.toLowerCase() === "n/a"));
  return parts.length ? parts.join(", ") : "";
});
const morphoGenderClass = computed(() => {
  const g = String(currentCard.value?.gender || "").toLowerCase();
  if (g === "feminine") return "morpho-feminine";
  if (g === "masculine") return "morpho-masculine";
  return "";
});

// Using shared splitSentence from "@/lib/sentenceUtils"

// Audio: URL-based only. Provide audio URLs on demo cards in LandingView.vue
// Add language_sentence_audio and language_word_audio to each demo card.
// Returns a promise that resolves after playback completes or a fallback minimum duration.
function playUrlAudio(url, minMs = 1000) {
  if (url) {
    try {
      // Prefer a warmed URL if preloaded; still create a fresh element for stateless playback
      const warmed = audioPreloader?.get ? audioPreloader.get(url) : null;
      const audio = new Audio((warmed && warmed.url) || url);
      try {
        audio.preload = "auto";
        audio.crossOrigin = "anonymous";
      } catch {
        /* ignore */
      }
      return new Promise((resolve) => {
        let ended = false;
        const cleanup = () => {
          if (ended) return;
          ended = true;
          audio.removeEventListener("ended", onEnded);
          audio.removeEventListener("error", onError);
          resolve();
        };
        const onEnded = cleanup;
        const onError = cleanup;
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("error", onError);
        audio.play().catch(onError);
        // Ensure we don't cut off instantaneously; keep at least minMs
        setTimeout(() => {
          // If already ended, resolve is already called; else do nothing here.
        }, minMs);
      });
    } catch (_e) {
      // Fall back to a minimal delay if element cannot play
    }
  }
  // No audio available; just delay for minMs so the highlight is visible
  return new Promise((r) => setTimeout(r, minMs));
}

// Focus input when card changes
watch(
  () => [currentIndex.value, currentCard.value],
  () => {
    attemptStartedAt.value = Date.now();
    // Focus the blank if present
    nextTick().then(() => {
      const el = Array.isArray(inputEl.value)
        ? inputEl.value[0]
        : inputEl.value;
      if (el && typeof el.focus === "function") {
        el.focus();
        if (typeof el.select === "function") el.select();
      }
      applyDynamicWidth();
    });
  },
  { immediate: true }
);

// Answer checking
/* moved to shared utils */

async function checkAnswer() {
  const card = currentCard.value;
  if (!card) return;

  // Avoid re-submitting while showing incorrect state
  if (
    answerStatus.value === "incorrect" ||
    (props.singleAttempt && firstAttemptResolved.value)
  )
    return;

  const enteredWordRaw = String(inputValue.value || "").trim();
  const correctWordRaw = String(card.language_word || "");
  const enteredWord = stripDiacritics(enteredWordRaw).toLowerCase();
  const correctWord = stripDiacritics(correctWordRaw).toLowerCase();

  if (!enteredWord) {
    // Treat Enter on empty input as incorrect (demo + onboarding)
    answerStatus.value = "incorrect";
    if (props.singleAttempt) firstAttemptResolved.value = true;

    {
      const parts = splitSentence(card);
      const before = Array.isArray(parts) ? parts[0] : "";
      const isAtStart =
        Array.isArray(parts) &&
        parts.length >= 2 &&
        parts[1] === "__BLANK__" &&
        stripLeadingPunctuation((before || "").trim()) === "";
      const original =
        card?.language === "zh"
          ? card?.pinyin || card?.language_word || ""
          : card?.language_word || "";
      const display =
        isAtStart && original
          ? original[0].toUpperCase() + original.slice(1)
          : original;
      inputValue.value = display;
      await nextTick();
      applyDynamicWidth();
    }

    // Ensure highlight is visible; play word audio if available
    await playUrlAudio(card.language_word_audio || "", 700);

    // Emit result
    emit("card-reviewed", {
      id: card.id,
      correct: false,
      durationMs: null,
    });

    if (props.singleAttempt) {
      // Parent will load the next card; keep the revealed word visible until then
      return;
    }

    // Non-singleAttempt: clear and let the user try again
    inputValue.value = "";
    answerStatus.value = null;
    await nextTick();
    const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
    if (el && typeof el.focus === "function") el.focus();
    applyDynamicWidth();
    return;
  }

  const startMs = Date.now();

  // Correct
  if (enteredWord === correctWord) {
    answerStatus.value = "correct";
    if (props.singleAttempt) firstAttemptResolved.value = true;

    // Fill the blank with display capitalization when the word is at sentence start
    {
      const parts = splitSentence(card);
      const before = Array.isArray(parts) ? parts[0] : "";
      const isAtStart =
        Array.isArray(parts) &&
        parts.length >= 2 &&
        parts[1] === "__BLANK__" &&
        stripLeadingPunctuation((before || "").trim()) === "";
      const display =
        isAtStart && correctWordRaw
          ? correctWordRaw[0].toUpperCase() + correctWordRaw.slice(1)
          : correctWordRaw;
      inputValue.value = display;
      await nextTick();
      applyDynamicWidth();
    }

    // Audio: URL-based only (ensure highlight remains visible at least ~1200ms)
    await playUrlAudio(card.language_sentence_audio || "", 1200);

    // Emit result; in single-attempt mode keep the entered word visible and let parent load the next card
    emit("card-reviewed", {
      id: card.id,
      correct: true,
      durationMs: props.singleAttempt ? null : Date.now() - startMs,
    });

    if (!props.singleAttempt) {
      inputValue.value = "";
      answerStatus.value = null;
      isSynonymAnswer.value = false;
      nextCard();
      return;
    }
    // In single-attempt mode, do not clear or advance here. The parent will replace this component with the next card.
    return;
  }

  // Synonym (neutral)
  if (
    Array.isArray(card.language_word_synonyms) &&
    card.language_word_synonyms
      .map((s) => stripDiacritics(String(s)).toLowerCase().trim())
      .includes(enteredWord) &&
    enteredWord !== correctWord
  ) {
    isSynonymAnswer.value = true;
    emit("synonym-entered");
    setTimeout(() => {
      inputValue.value = "";
      isSynonymAnswer.value = false;
      applyDynamicWidth();
      // keep focus on input
      const el = Array.isArray(inputEl.value)
        ? inputEl.value[0]
        : inputEl.value;
      if (el && typeof el.focus === "function") el.focus();
    }, 1500);
    await nextTick();
    applyDynamicWidth();
    return;
  }

  // Incorrect
  answerStatus.value = "incorrect";
  if (props.singleAttempt) firstAttemptResolved.value = true;

  // Show the correct word (or pinyin if provided) and highlight briefly
  {
    const parts = splitSentence(card);
    const before = Array.isArray(parts) ? parts[0] : "";
    const isAtStart =
      Array.isArray(parts) &&
      parts.length >= 2 &&
      parts[1] === "__BLANK__" &&
      stripLeadingPunctuation((before || "").trim()) === "";
    const original =
      card?.language === "zh"
        ? card?.pinyin || card?.language_word || ""
        : card?.language_word || "";
    const display =
      isAtStart && original
        ? original[0].toUpperCase() + original.slice(1)
        : original;
    inputValue.value = display;
    await nextTick();
    applyDynamicWidth();
  }

  // Play the word audio (URL-based only); ensure at least ~700ms of highlight time
  await playUrlAudio(card.language_word_audio || "", 700);

  // Emit result; in single-attempt mode keep the revealed word visible until the next card loads
  emit("card-reviewed", {
    id: card.id,
    correct: false,
    durationMs: props.singleAttempt ? null : Date.now() - startMs,
  });

  if (props.singleAttempt) {
    // Parent will load the next card; keep current input visible until then
    return;
  }

  // Clear and let user try again (do not auto-advance on incorrect)
  inputValue.value = "";
  answerStatus.value = null;
  await nextTick();
  const el = Array.isArray(inputEl.value) ? inputEl.value[0] : inputEl.value;
  if (el && typeof el.focus === "function") el.focus();
  applyDynamicWidth();
}

function nextCard() {
  const total = currentFlashcards.value.length;
  if (currentIndex.value < total - 1) {
    currentIndex.value++;
  }
  // Reset per-card attempt state and inputs
  if (props.singleAttempt) firstAttemptResolved.value = false;
  inputValue.value = "";
  answerStatus.value = null;
  isSynonymAnswer.value = false;
  emit("index-changed", { index: currentIndex.value, total });
}

// Load cards on mount
onMounted(() => {
  // Demo mode: use provided cards only (no network)
  if (demoMode.value) {
    currentFlashcards.value = props.preloadedCards.slice(
      0,
      Math.max(1, props.limit)
    );
    emit("loaded", { total: currentFlashcards.value.length });
    return;
  }

  // Non-demo mode: leave population to parent or a future data source.
  // For safety, start with an empty list (parent can pass cards via props in a future enhancement).
  currentFlashcards.value = [];
  emit("loaded", { total: 0 });
});

// Expose small API for parents
defineExpose({
  focusInput: () => {
    nextTick().then(() => {
      const el = Array.isArray(inputEl.value)
        ? inputEl.value[0]
        : inputEl.value;
      if (el && typeof el.focus === "function") el.focus();
      applyDynamicWidth();
    });
  },
  nextCard,
  checkAnswer,
  hasInput: () =>
    !!(
      typeof inputValue.value === "string" && inputValue.value.trim().length > 0
    ),
});
</script>

<template>
  <div v-if="!bare" class="flashcard-container">
    <div v-if="currentCard" class="flashcard-card" style="position: relative">
      <!-- State dots -->
      <div
        v-if="showStateDots"
        class="state-dots"
        style="
          position: absolute;
          top: 30px;
          left: 15px;
          display: flex;
          gap: 6px;
        "
        aria-hidden="true"
      >
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 1 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 2 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 3 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
      </div>

      <!-- Sentence with blank -->
      <p class="sentence-blank">
        <template v-for="(part, i) in splitSentence(currentCard)" :key="i">
          <input
            ref="inputEl"
            v-focus
            v-if="part === '__BLANK__'"
            type="text"
            class="word-blank"
            autocapitalize="none"
            autocorrect="off"
            spellcheck="false"
            autocomplete="off"
            inputmode="text"
            enterkeyhint="done"
            :readonly="!!answerStatus"
            :key="`blank-${currentIndex}-${i}`"
            :class="{
              'correct-answer': answerStatus === 'correct',
              'incorrect-answer': answerStatus === 'incorrect',
              'synonym-answer': isSynonymAnswer,
            }"
            v-model="inputValue"
            @input="onInputRaw"
            @compositionstart="onCompositionStart"
            @compositionupdate="onCompositionUpdate"
            @compositionend="onCompositionEnd"
            @keyup.enter="checkAnswer"
          />
          <span v-else>{{ part }}</span>
        </template>
      </p>

      <!-- Morphology -->
      <div
        v-if="showMorpho && morphoInfo"
        class="morpho-box"
        :class="morphoGenderClass"
        aria-label="part of speech and tense"
      >
        <span class="morpho-chip">{{ morphoInfo }}</span>
      </div>

      <!-- Translations -->
      <div class="translations">
        <p class="word-translation">
          {{ currentCard.language_word_mappings?.en }}
        </p>
        <p class="sentence-translation" v-show="showSentenceTranslation">
          {{ currentCard.language_sentence_mappings?.en }}
        </p>
        <p v-if="isSynonymAnswer" class="synonym-note">
          "{{ inputValue }}" is a synonym.
        </p>
        <button
          class="translate-toggle"
          :aria-expanded="showSentenceTranslation"
          aria-label="Toggle sentence translation"
          @click="showSentenceTranslation = !showSentenceTranslation"
        >
          <img
            src="@/assets/RightArrow.svg"
            alt=""
            :class="{ rotated: showSentenceTranslation }"
          />
        </button>
      </div>
    </div>

    <div v-else class="flashcard-placeholder">
      <div class="placeholder-card">
        <div class="placeholder-line w-80"></div>
        <div class="placeholder-line w-60"></div>
        <div class="placeholder-line w-40"></div>
      </div>
    </div>
  </div>
  <template v-else>
    <div v-if="currentCard" class="flashcard-card" style="position: relative">
      <!-- State dots -->
      <div
        v-if="showStateDots"
        class="state-dots"
        style="
          position: absolute;
          top: 30px;
          left: 20px;
          display: flex;
          gap: 6px;
        "
        aria-hidden="true"
      >
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 1 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 2 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
        <span
          :style="{
            width: '18px',
            height: '4px',
            borderRadius: '20%',
            background: activeDots >= 3 ? '#8AD1A9' : '#ddd',
            display: 'inline-block',
          }"
        />
      </div>

      <!-- Sentence with blank -->
      <p class="sentence-blank">
        <template v-for="(part, i) in splitSentence(currentCard)" :key="i">
          <input
            ref="inputEl"
            v-focus
            v-if="part === '__BLANK__'"
            type="text"
            class="word-blank"
            autocapitalize="none"
            autocorrect="off"
            spellcheck="false"
            autocomplete="off"
            inputmode="text"
            enterkeyhint="done"
            :readonly="!!answerStatus"
            :key="`blank-${currentIndex}-${i}`"
            :class="{
              'correct-answer': answerStatus === 'correct',
              'incorrect-answer': answerStatus === 'incorrect',
              'synonym-answer': isSynonymAnswer,
            }"
            v-model="inputValue"
            @input="onInputRaw"
            @compositionstart="onCompositionStart"
            @compositionupdate="onCompositionUpdate"
            @compositionend="onCompositionEnd"
            @keyup.enter="checkAnswer"
          />
          <span v-else>{{ part }}</span>
        </template>
      </p>

      <!-- Morphology -->
      <div
        v-if="showMorpho && morphoInfo"
        class="morpho-box"
        :class="morphoGenderClass"
        aria-label="part of speech and tense"
      >
        <span class="morpho-chip">{{ morphoInfo }}</span>
      </div>

      <!-- Translations -->
      <div class="translations">
        <p class="word-translation">
          {{ currentCard.language_word_mappings?.en }}
        </p>
        <p class="sentence-translation" v-show="showSentenceTranslation">
          {{ currentCard.language_sentence_mappings?.en }}
        </p>
        <p v-if="isSynonymAnswer" class="synonym-note">
          "{{ inputValue }}" is a synonym.
        </p>
        <button
          class="translate-toggle"
          :aria-expanded="showSentenceTranslation"
          aria-label="Toggle sentence translation"
          @click="showSentenceTranslation = !showSentenceTranslation"
        >
          <img
            src="@/assets/RightArrow.svg"
            alt=""
            :class="{ rotated: showSentenceTranslation }"
          />
        </button>
      </div>
    </div>

    <div v-else class="flashcard-placeholder">
      <div class="placeholder-card">
        <div class="placeholder-line w-80"></div>
        <div class="placeholder-line w-60"></div>
        <div class="placeholder-line w-40"></div>
      </div>
    </div>
  </template>
</template>

<style scoped>
/* Container */
.flashcard-container {
  flex: 1;
  display: block;
  padding: 2rem 1rem;
}

/* Card */
.flashcard-card {
  background: var(--sidebar-bg, #fff);
  color: var(--text-color, #2c3e50);
  border-radius: 15px;
  box-shadow: none;
  padding: 0;
  width: 650px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 350px;
}

/* Placeholder while loading */
.flashcard-placeholder {
  display: grid;
  place-items: center;
  padding: 24px 0;
}
.placeholder-card {
  width: 650px;
  max-width: 100%;
  background: var(--sidebar-bg, #fff);
  border-radius: 15px;
  padding: 24px;
}
.placeholder-line {
  height: 14px;
  border-radius: 8px;
  background: color-mix(in oklab, var(--hover-bg, #f1f3f5) 70%, transparent);
  margin: 10px 0;
}
.placeholder-line.w-80 {
  width: 80%;
}
.placeholder-line.w-60 {
  width: 60%;
}
.placeholder-line.w-40 {
  width: 40%;
}

/* Sentence and blank input */
.sentence-blank {
  flex: 0 0 auto;
  padding: 3rem;
  padding-left: 2rem;
  padding-bottom: 15px;
  font-size: 2rem;
  color: var(--text-color, #2c3e50);
  line-height: 1.6;
  display: block;
  word-break: break-word;
  overflow-wrap: anywhere;
}

input.word-blank {
  font-family: inherit;
  font-size: inherit;
  border: none;
  border-radius: 5px;
  padding: 2px 5px;
  line-height: 1.2;
  transition: background-color 0.3s ease;
  max-width: 100%;
  display: inline-block;
  box-sizing: content-box;
  overflow: hidden;
  background: var(--hover-bg, #f1f3f5);
  color: var(--text-color, #2c3e50);
  outline: none;
  box-shadow: none;
}

input.word-blank:focus:not(.correct-answer):not(.incorrect-answer):not(
    .synonym-answer
  ) {
  background: var(--hover-bg, #f1f3f5);
  outline: none;
  box-shadow: none;
}

/* Correct, incorrect, synonym animations */
input.word-blank.correct-answer,
input.word-blank.correct-answer:focus {
  animation: flash-correct 1800ms ease-in-out;
}
@keyframes flash-correct {
  0% {
    background-color: var(--hover-bg, #f1f3f5);
    color: inherit;
  }
  10%,
  70% {
    background-color: #a8e6cf;
    color: #2c3e50;
  }
  100% {
    background-color: var(--hover-bg, #f1f3f5);
    color: inherit;
  }
}

input.word-blank.incorrect-answer,
input.word-blank.incorrect-answer:focus {
  animation: flash-incorrect 700ms ease-in-out;
}
@keyframes flash-incorrect {
  0% {
    background-color: var(--hover-bg, #f1f3f5);
  }
  10%,
  70% {
    background-color: #ffaaa5;
  }
  100% {
    background-color: var(--hover-bg, #f1f3f5);
  }
}

input.word-blank.synonym-answer,
input.word-blank.synonym-answer:focus {
  animation: flash-synonym 1200ms ease-in-out;
}
@keyframes flash-synonym {
  0% {
    background-color: var(--hover-bg, #f1f3f5);
  }
  10%,
  70% {
    background-color: #fff3b0;
  }
  100% {
    background-color: var(--hover-bg, #f1f3f5);
  }
}

.synonym-note {
  font-size: 0.9rem;
  color: #8a8a8a;
  margin: 6px 0 0 0;
}

/* Morphology */
.morpho-box {
  margin-left: 30px;
  margin-bottom: 20px;
  display: inline-flex;
  background: var(--hover-bg, #f5f5f5);
  border: 0;
  border-radius: 6px;
  padding: 5px 15px;
  color: var(--text-color, #2c3e50);
  font-size: 1.1rem;
  font-weight: 200;
  align-items: center;
  width: -moz-fit-content;
  width: fit-content;
  height: 30px;
  max-width: 100%;
}
.morpho-box .morpho-chip {
  white-space: nowrap;
}
.morpho-box.morpho-feminine {
  background: var(--morpho-feminine-bg, #f9e1ef);
}
.morpho-box.morpho-masculine {
  background: var(--morpho-masculine-bg, #e1e9f9);
}

/* Translations */
.translations {
  flex: 0 0 auto;
  background: var(--hover-bg, #f7f8fa);
  padding: 1.5rem 2rem;
  border-radius: 0 0 15px 15px;
  text-align: left;
  color: var(--text-color, #2c3e50);
  position: relative;
  margin-top: auto;
}
.word-translation {
  font-size: 1.5rem;
  margin-bottom: 0.3rem;
  margin-top: 0.2rem;
  font-weight: 200;
}
.sentence-translation {
  font-size: 1.1rem;
  line-height: 1.5;
  margin: 0;
  font-weight: 200;
}

/* Translate toggle */
.translate-toggle {
  position: absolute;
  right: 12px;
  top: 12px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
}
.translate-toggle img {
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
}
.translate-toggle img.rotated {
  transform: rotate(90deg);
}

/* Responsive */
@media (max-width: 768px) {
  .flashcard-card {
    width: 100%;
    max-width: 650px;
    margin: 1rem auto;
    box-sizing: border-box;
    overflow: hidden;
    min-height: 0;
  }
  .translations {
    padding: 0.75rem 1rem;
    height: auto;
  }
  .sentence-blank {
    font-size: 1.4rem;
    max-height: 80px;
    padding-top: 35px;
    padding-left: 15px;
    padding-right: 15px;
    padding-bottom: 15px;
  }
  .word-translation {
    font-size: 1.1rem;
  }
  .sentence-translation {
    font-size: 1rem;
  }
  .morpho-box {
    margin-left: 15px;
  }
}
</style>
